import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, UserPlus } from "lucide-react";
import * as React from "react";

import type { AdminAddMemberResult, AdminOrganization, OrgRole } from "@ando/core";
import { addAdminOrgMember } from "@ando/core";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  useToast,
} from "@ando/ui";

import { adminOrgKeys } from "@/features/admin/hooks/useAdminOrg";

/**
 * AddMemberDialog — add an existing user to this org by email.
 *
 * The BE endpoint is a smart dispatcher:
 *   - ``action: "added"``       → user existed + no current org → membership row.
 *   - ``action: "reactivated"`` → previously-removed member restored in place.
 *   - ``action: "invited"``     → user did not exist → invite email sent.
 *   - ``action: "migration_required"`` → user belongs to another org. Cross-org
 *                                 move is a dedicated platform-admin migration
 *                                 (``migrate_user_to_org``), NOT an invite — we
 *                                 show a notice pointing to the migration flow.
 *
 * After success we invalidate both members and invites since the BE may
 * have produced rows in either bucket.
 */

export interface AddMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  org: AdminOrganization;
}

interface FormState {
  email: string;
  role: OrgRole;
  surface: "workspace" | "engine" | "both";
}

const INITIAL: FormState = { email: "", role: "member", surface: "workspace" };

export function AddMemberDialog({
  open,
  onOpenChange,
  org,
}: AddMemberDialogProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [form, setForm] = React.useState<FormState>(INITIAL);
  const [pendingMove, setPendingMove] = React.useState<{
    email: string;
    currentOrgName: string;
  } | null>(null);

  React.useEffect(() => {
    if (open) {
      setForm(INITIAL);
      setPendingMove(null);
    }
  }, [open]);

  const mutation = useMutation<
    AdminAddMemberResult,
    Error,
    { email: string; role: OrgRole; surface: FormState["surface"] }
  >({
    mutationFn: (payload) =>
      addAdminOrgMember(org.id, {
        email: payload.email.trim(),
        role: payload.role,
        product_access: payload.surface,
      }),
    onSuccess: (result) => {
      switch (result.action) {
        case "added":
          toast.success(i18n._(msg`Membro aggiunto`), {
            description: `${form.email.trim()} → ${org.name}`,
          });
          qc.invalidateQueries({ queryKey: adminOrgKeys.members(org.id) });
          onOpenChange(false);
          return;
        case "reactivated":
          // Re-adding a previously-removed member restores the row in place.
          toast.success(i18n._(msg`Membro riattivato`), {
            description: i18n._(msg`${form.email.trim()} è di nuovo membro di ${org.name}.`),
          });
          qc.invalidateQueries({ queryKey: adminOrgKeys.members(org.id) });
          onOpenChange(false);
          return;
        case "invited":
          toast.success(i18n._(msg`Invito inviato`), {
            description: i18n._(msg`${form.email.trim()} riceverà un'email di invito.`),
          });
          qc.invalidateQueries({ queryKey: adminOrgKeys.invites(org.id) });
          onOpenChange(false);
          return;
        case "migration_required":
          // User already belongs to another org. Cross-org move is a dedicated
          // migration (platform-admin ``migrate_user_to_org``), NOT an invite —
          // inform the admin instead of relocating from here.
          setPendingMove({
            email: form.email.trim(),
            currentOrgName: result.current_org.name,
          });
          return;
      }
    },
    onError: (err) => {
      toast.error(i18n._(msg`Operazione fallita`), {
        description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso.`),
      });
    },
  });

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    const email = form.email.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(i18n._(msg`Email non valida`), {
        description: i18n._(msg`Inserisci un'email valida.`),
      });
      return;
    }
    mutation.mutate({ email, role: form.role, surface: form.surface });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={16} aria-hidden />
              {i18n._(msg`Aggiungi membro`)}
            </DialogTitle>
            <DialogDescription>
              {i18n._(
                msg`Aggiungi un utente a ${org.name}. Se l'email non esiste viene inviato un invito.`,
              )}
            </DialogDescription>
          </DialogHeader>

          {pendingMove ? (
            <div
              className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-[12.5px] text-ink-1"
              role="alert"
            >
              <p>
                <strong>{pendingMove.email}</strong> {i18n._(msg`appartiene già a`)}{" "}
                <strong>{pendingMove.currentOrgName}</strong>
                {". "}
                {i18n._(
                  msg`Lo spostamento tra organizzazioni è una migrazione dedicata — usa il flusso di migrazione utente.`,
                )}
              </p>
            </div>
          ) : null}

          <div className="space-y-3">
            <div className="space-y-1.5">
              <label
                htmlFor="add-member-email"
                className="block font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
              >
                {i18n._(msg`Email`)}
              </label>
              <input
                id="add-member-email"
                type="email"
                value={form.email}
                onChange={(e) => {
                  setForm((f) => ({ ...f, email: e.target.value }));
                  // Typing a different email clears the migration notice.
                  setPendingMove(null);
                }}
                placeholder="user@example.com"
                autoComplete="off"
                className="h-9 w-full rounded-md border border-hairline bg-card px-3 text-[13px] text-ink-1 placeholder:text-ink-3 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="add-member-role"
                className="block font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
              >
                {i18n._(msg`Ruolo`)}
              </label>
              <Select
                value={form.role}
                onValueChange={(value) => setForm((f) => ({ ...f, role: value as OrgRole }))}
              >
                <SelectTrigger id="add-member-role" ariaLabel={i18n._(msg`Ruolo`)} size="lg" />
                <SelectContent>
                  <SelectItem value="member">{i18n._(msg`Member`)}</SelectItem>
                  <SelectItem value="admin">{i18n._(msg`Admin`)}</SelectItem>
                  <SelectItem value="owner">{i18n._(msg`Owner`)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="add-member-surface"
                className="block font-semibold text-[10px] text-ink-3 uppercase tracking-wide"
              >
                {i18n._(msg`Accesso`)}
              </label>
              <Select
                value={form.surface}
                onValueChange={(value) =>
                  setForm((f) => ({
                    ...f,
                    surface: value as FormState["surface"],
                  }))
                }
              >
                <SelectTrigger id="add-member-surface" ariaLabel={i18n._(msg`Accesso`)} size="lg" />
                <SelectContent>
                  <SelectItem value="workspace">{i18n._(msg`Solo Workspace`)}</SelectItem>
                  <SelectItem value="engine">{i18n._(msg`Solo Engine`)}</SelectItem>
                  <SelectItem value="both">{i18n._(msg`Workspace ed Engine`)}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-ink-3">
                {form.surface === "both"
                  ? i18n._(
                      msg`Accesso a Workspace, Console e API. Il ruolo gestisce i permessi, non i prodotti disponibili.`,
                    )
                  : form.surface === "engine"
                    ? i18n._(msg`Accesso tecnico alla Console di configurazione e alle API.`)
                    : i18n._(msg`Accesso all'app Ando per il lavoro quotidiano.`)}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {i18n._(msg`Annulla`)}
            </Button>
            <Button type="submit" disabled={mutation.isPending || !form.email}>
              {mutation.isPending ? (
                <Loader2 size={14} className="mr-2 animate-spin" aria-hidden />
              ) : null}
              {i18n._(msg`Aggiungi`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
